import { useDispatch, useSelector } from 'react-redux';
import { CurrencyInput } from '..';
import { MethodologyCallout } from '@/shared/components/methodology-callout';
import {
  selectClient,
  putClientCareEnvironmentCostsForClientByClientIdRequest,
  selectClientLocalCareEnvironmentCosts,
} from '../model';
import { FormErrorAlert } from './FormErrorAlert';
import { CareEnvironment, careEnvironmentDefs, formatCurrency, FundingPolicyType } from '@shared';
import { useEffect, useState } from 'react';
import { DialogModal } from './DialogModal';
import { CostDataMethodologySlideOutContent } from '@/components/LearnMoreSlideOutContent';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface CareSettingCostModalProps {
  selectedCareEnvironment: CareEnvironment;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const ErrorMessage = ({ message }: { message?: string }) => {
  if (!message) {
    return null;
  } else {
    return <p className="ml-2 text-sm text-red-400">{message}</p>;
  }
};

export default function CareEnvironmentCostModal({
  selectedCareEnvironment,
  open,
  setOpen,
}: CareSettingCostModalProps) {
  const client = useSelector(selectClient);
  const clientLocalCareEnvironmentCosts = useSelector(selectClientLocalCareEnvironmentCosts);
  const dispatch = useDispatch();
  const { careEnvironmentCosts } = client;

  const requiredKeys: CareEnvironment[] = [selectedCareEnvironment];

  const [careEnvironmentCostsState, setCareEnvironmentCostsState] = useState<CareEnvironmentCosts>(
    careEnvironmentCosts ?? {},
  );

  const [formErrors, setFormErrors] = useState({} as Record<CareEnvironment, string>);
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);
  const [useLocalRateChecked, setUseLocalRateChecked] = useState(
    careEnvironmentCostsState[selectedCareEnvironment] == null,
  );
  const localRateData = clientLocalCareEnvironmentCosts[selectedCareEnvironment];
  const hasCqnDiscount =
    localRateData?.discount?.policyType === FundingPolicyType.careScoutCareAssurance;
  const handleChangeCareEnvironmentCosts = (value: number) => {
    setCareEnvironmentCostsState(prevCareEnvironmentCostsState => {
      return {
        ...prevCareEnvironmentCostsState,
        [selectedCareEnvironment]: value,
      };
    });
  };

  const validateForm = () => {
    let isFormValid = true;

    const errors = {} as Record<CareEnvironment, string>;
    requiredKeys.forEach(key => {
      // Field is required unless checkbox is checked (useLocalRateChecked)
      if (!useLocalRateChecked && !careEnvironmentCostsState[key]) {
        errors[key] = 'Required Field';
      }
    });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      isFormValid = false;
    }
    return isFormValid;
  };

  const handleClickSubmit = () => {
    setFormHasBeenSubmitted(true);
    const isFormValid = validateForm();
    if (!isFormValid) {
      return;
    }
    dispatch(
      putClientCareEnvironmentCostsForClientByClientIdRequest({
        clientId: client.clientId,
        careEnvironmentCosts: careEnvironmentCostsState,
      }),
    );
    setOpen(false);
  };

  const formIsValid = Object.keys(formErrors).length === 0;
  const hasRequiredFieldsErrors = Object.keys(formErrors).some(key =>
    requiredKeys.includes(key as unknown as CareEnvironment),
  );

  const resetForm = () => {
    setCareEnvironmentCostsState(careEnvironmentCosts ?? {});
    setFormErrors({} as Record<CareEnvironment, string>);
  };

  const onClose = () => {
    setOpen(false);
    resetForm();
  };

  useEffect(() => {
    if (formHasBeenSubmitted) {
      validateForm();
    }
  }, [careEnvironmentCostsState, useLocalRateChecked]);

  useEffect(() => {
    if (open) {
      setCareEnvironmentCostsState(careEnvironmentCosts ?? {});
      setFormHasBeenSubmitted(false);
    }
  }, [open]);

  useEffect(() => {
    const currentValue = careEnvironmentCostsState[selectedCareEnvironment];
    setUseLocalRateChecked(currentValue === null || currentValue === undefined);
  }, [open, selectedCareEnvironment, careEnvironmentCostsState]);

  const unitDisplay = selectedCareEnvironment === CareEnvironment.home ? 'hr' : 'month';

  const modalContent = (
    <>
      <div className="relative pb-8">
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 py-7">
          <div className="col-start-1 col-end-3 flex flex-col gap-2 pb-4">
            <h4 className="flex items-center gap-2 text-lg text-gray-900">
              Set your {selectedCareEnvironment === CareEnvironment.home ? 'hourly' : 'monthly'}{' '}
              rate
              <MethodologyCallout
                content={<CostDataMethodologySlideOutContent />}
                title="Cost Data"
                variant="icon"
                iconClassName="size-4"
              />
            </h4>
            <div className="flex items-center gap-1 text-sm text-gray-700">
              <span>Default rate per {localRateData?.rateLocaleDescription?.toLowerCase()}:</span>
              <span className="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-800">
                {formatCurrency(localRateData?.rateAmount)}/{unitDisplay}
              </span>
            </div>
          </div>
          <div>
            <CurrencyInput
              label={
                selectedCareEnvironment === CareEnvironment.home
                  ? 'CUSTOM HOURLY RATE'
                  : 'CUSTOM MONTHLY RATE'
              }
              unit={selectedCareEnvironment === CareEnvironment.home ? 'per hour' : 'per month'}
              placeholder="Enter dollars"
              value={careEnvironmentCostsState[selectedCareEnvironment] ?? null}
              onChange={handleChangeCareEnvironmentCosts}
              disabled={useLocalRateChecked}
            />
            <ErrorMessage message={formErrors[selectedCareEnvironment]} />
            <div className="mt-3 flex items-center gap-3">
              <Checkbox
                id="use-local-rate"
                checked={useLocalRateChecked}
                onCheckedChange={checked => {
                  const isChecked = Boolean(checked);
                  setUseLocalRateChecked(isChecked);
                  setCareEnvironmentCostsState(prev => ({
                    ...prev,
                    [selectedCareEnvironment]: isChecked
                      ? null
                      : (prev[selectedCareEnvironment] ?? localRateData.rateAmount),
                  }));
                }}
              />
              <Label
                htmlFor="use-local-rate"
                className="flex flex-col  gap-0.5 text-sm text-gray-700"
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium text-gray-800">Use default rate</span>
                  <span className="text-gray-700">
                    ({formatCurrency(localRateData?.rateAmount)}/{unitDisplay})
                  </span>
                </span>
                {hasCqnDiscount && (
                  <span className="block font-normal text-gray-700">
                    Default rate applies CQN savings. To turn off projected CQN savings, uncheck
                    this box and enter a custom rate.
                  </span>
                )}
              </Label>
            </div>
          </div>
        </div>
      </div>
      {!formIsValid && (
        <FormErrorAlert mainErrorMessage="There is an error with your submission">
          {hasRequiredFieldsErrors && (
            <li>
              <span className="font-bold">Required field:</span> Please fill out all required
              fields.
            </li>
          )}
        </FormErrorAlert>
      )}
      <div className="mt-5 flex flex-row-reverse">
        <button
          type="button"
          className="bg-primary hover:bg-primary inline-flex w-full justify-center rounded-md px-12 py-2 text-lg font-semibold text-white shadow-sm sm:ml-3 sm:w-auto"
          onClick={handleClickSubmit}
        >
          Save
        </button>
      </div>
    </>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title={`Edit ${careEnvironmentDefs[selectedCareEnvironment].label} Cost`}
      subTitle=""
      width="md"
    >
      {modalContent}
    </DialogModal>
  );
}
